import { cur } from '@/lib/currency';
import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { ExportButtons } from '@/components/ExportButtons';
import { DateFilter } from '@/components/DateFilter';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Scale, Plus, Search, Edit2, Trash2, RefreshCw, Barcode, Tag,
  Package, Loader2
} from 'lucide-react';
import api from '@/lib/api';

const UNITS = ['kg', 'g', 'lb', 'oz', 'liter', 'ml', 'piece'];
const CATEGORIES = ['Fruits', 'Vegetables', 'Meat', 'Seafood', 'Dairy', 'Bakery', 'Deli', 'Bulk Grains', 'Spices', 'Nuts', 'Other'];

const emptyForm = {
  name: '', barcode: '', price_per_unit: '', unit: 'kg',
  category: '', branch_id: '', tare_weight: '', min_weight: '',
};

function WeightItemCard({ item, onEdit, onDelete }) {
  return (
    <div className="border rounded-xl p-4 hover:shadow-md transition-all dark:border-stone-700 dark:bg-foreground" data-testid={`weight-item-${item.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm font-outfit dark:text-white">{item.name}</span>
            <Badge variant="secondary" className="text-[10px] capitalize">{item.category || 'Uncategorized'}</Badge>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1 font-semibold text-success">
              <Tag size={11} />{cur()} {Number(item.price_per_unit).toFixed(2)}/{item.unit}
            </span>
            {item.barcode && <span className="flex items-center gap-1"><Barcode size={11} />{item.barcode}</span>}
            {item.tare_weight > 0 && <span>Tare: {item.tare_weight}{item.unit}</span>}
            {item.min_weight > 0 && <span>Min: {item.min_weight}{item.unit}</span>}
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onEdit(item)} data-testid={`edit-wi-${item.id}`}><Edit2 size={13} /></Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-error" onClick={() => onDelete(item.id)} data-testid={`delete-wi-${item.id}`}><Trash2 size={13} /></Button>
        </div>
      </div>
    </div>
  );
}

export default function WeightItemsPage() {
  const [items, setItems] = useState([]);
  const [dateFilter, setDateFilter] = useState({ start: null, end: null, period: "all" });
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterBranch, setFilterBranch] = useState('all');

  const fetchData = async () => {
    try {
      const [itemsRes, brRes] = await Promise.all([
        api.get('/pos/weight-items'),
        api.get('/branches'),
      ]);
      setItems(itemsRes.data);
      setBranches(brRes.data);
    } catch { toast.error('Failed to load weight items'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openNew = () => {
    setEditingItem(null);
    setForm({ ...emptyForm, branch_id: branches[0]?.id || '' });
    setShowDialog(true);
  };
  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      name: item.name || '', barcode: item.barcode || '',
      price_per_unit: item.price_per_unit || '', unit: item.unit || 'kg',
      category: item.category || '', branch_id: item.branch_id || '',
      tare_weight: item.tare_weight || '', min_weight: item.min_weight || '',
    });
    setShowDialog(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Item name is required'); return; }
    if (!form.price_per_unit) { toast.error('Price per unit is required'); return; }
    try {
      const payload = {
        ...form,
        price_per_unit: parseFloat(form.price_per_unit),
        tare_weight: form.tare_weight ? parseFloat(form.tare_weight) : 0,
        min_weight: form.min_weight ? parseFloat(form.min_weight) : 0,
      };
      if (editingItem) {
        await api.put(`/pos/weight-items/${editingItem.id}`, payload);
        toast.success('Weight item updated');
      } else {
        await api.post('/pos/weight-items', payload);
        toast.success('Weight item added');
      }
      setShowDialog(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this weight item?')) return;
    try {
      await api.delete(`/pos/weight-items/${id}`);
      toast.success('Weight item deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const filtered = items.filter(i => {
    if (filterCategory !== 'all' && i.category !== filterCategory) return false;
    if (filterBranch !== 'all' && i.branch_id !== filterBranch) return false;
    if (search) {
      const q = search.toLowerCase();
      return (i.name || '').toLowerCase().includes(q) || (i.barcode || '').toLowerCase().includes(q);
    }
    return true;
  });

  const categories = [...new Set(items.map(i => i.category).filter(Boolean))];
  const categoryGroups = {};
  filtered.forEach(i => {
    const cat = i.category || 'Uncategorized';
    if (!categoryGroups[cat]) categoryGroups[cat] = [];
    categoryGroups[cat].push(i);
  });

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64 text-muted-foreground"><Loader2 className="animate-spin mr-2" size={20} />Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="weight-items-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-4">

              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-lg shadow-orange-200/50">

                <Scale size={24} className="text-white" />

              </div>

              <div>

                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">Weight Items</h1>
            <DateFilter onFilterChange={setDateFilter} />
            <ExportButtons branchIds={filterBranch && filterBranch !== 'all' ? [filterBranch] : []} dataType="weight_items" label="Weight Items" />

                <p className="text-sm text-muted-foreground mt-0.5">Weight-based item management</p>

              </div>

            </div>
            <p className="text-sm text-muted-foreground">Manage price-by-weight products for supermarket POS</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchData} variant="outline" size="sm" className="rounded-xl"><RefreshCw size={14} /></Button>
            <Button onClick={openNew} className="rounded-xl" data-testid="new-weight-item-btn"><Plus size={14} className="mr-1" />Add Weight Item</Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card data-testid="stat-total-items"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-info/10 dark:bg-blue-900/20 rounded-lg"><Scale size={18} className="text-info" /></div><div><div className="text-2xl font-bold font-outfit">{items.length}</div><div className="text-xs text-muted-foreground">Total Items</div></div></div></CardContent></Card>
          <Card data-testid="stat-categories"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-warning/10 dark:bg-amber-900/20 rounded-lg"><Tag size={18} className="text-warning" /></div><div><div className="text-2xl font-bold font-outfit">{categories.length}</div><div className="text-xs text-muted-foreground">Categories</div></div></div></CardContent></Card>
          <Card data-testid="stat-barcoded"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-success/10 dark:bg-emerald-900/20 rounded-lg"><Barcode size={18} className="text-success" /></div><div><div className="text-2xl font-bold font-outfit">{items.filter(i => i.barcode).length}</div><div className="text-xs text-muted-foreground">With Barcode</div></div></div></CardContent></Card>
          <Card data-testid="stat-kg-items"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg"><Package size={18} className="text-purple-600" /></div><div><div className="text-2xl font-bold font-outfit">{items.filter(i => i.unit === 'kg').length}</div><div className="text-xs text-muted-foreground">By Weight (kg)</div></div></div></CardContent></Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or barcode..." className="pl-9 h-9 text-sm rounded-xl" data-testid="search-input" />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-36 h-9 text-sm rounded-xl" data-testid="filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          {branches.length > 1 && (
            <Select value={filterBranch} onValueChange={setFilterBranch}>
              <SelectTrigger className="w-36 h-9 text-sm rounded-xl" data-testid="filter-branch"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Items Grid */}
        {filtered.length === 0 ? (
          <Card><CardContent className="py-16 text-center">
            <Scale size={40} className="mx-auto mb-3 opacity-20" />
            <p className="text-muted-foreground text-sm">No weight items found</p>
            <Button onClick={openNew} variant="outline" className="mt-3 rounded-xl" size="sm"><Plus size={14} className="mr-1" />Add First Item</Button>
          </CardContent></Card>
        ) : (
          <div className="space-y-4">
            {Object.entries(categoryGroups).sort(([a], [b]) => a.localeCompare(b)).map(([cat, catItems]) => (
              <div key={cat}>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="text-xs font-medium">{cat} ({catItems.length})</Badge>
                  <div className="flex-1 h-px bg-muted dark:bg-stone-700" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {catItems.map(item => (
                    <WeightItemCard key={item.id} item={item} onEdit={openEdit} onDelete={handleDelete} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-md" data-testid="weight-item-dialog">
            <DialogHeader>
              <DialogTitle className="font-outfit">{editingItem ? 'Edit Weight Item' : 'Add Weight Item'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Item Name *</Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., Bananas" required data-testid="form-name" />
                </div>
                <div>
                  <Label>Barcode</Label>
                  <Input value={form.barcode} onChange={e => setForm({ ...form, barcode: e.target.value })} placeholder="Scan or enter" data-testid="form-barcode" />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="form-category"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Price per Unit *</Label>
                  <Input type="number" step="0.01" value={form.price_per_unit} onChange={e => setForm({ ...form, price_per_unit: e.target.value })} placeholder="SR" required data-testid="form-price" />
                </div>
                <div>
                  <Label>Unit</Label>
                  <Select value={form.unit} onValueChange={v => setForm({ ...form, unit: v })}>
                    <SelectTrigger data-testid="form-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tare Weight</Label>
                  <Input type="number" step="0.001" value={form.tare_weight} onChange={e => setForm({ ...form, tare_weight: e.target.value })} placeholder="Container weight" data-testid="form-tare" />
                </div>
                <div>
                  <Label>Min Weight</Label>
                  <Input type="number" step="0.001" value={form.min_weight} onChange={e => setForm({ ...form, min_weight: e.target.value })} placeholder="Minimum" data-testid="form-min-weight" />
                </div>
                <div>
                  <Label>Branch</Label>
                  <Select value={form.branch_id} onValueChange={v => setForm({ ...form, branch_id: v })}>
                    <SelectTrigger data-testid="form-branch"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-weight-item-btn">{editingItem ? 'Update' : 'Add Item'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
